import { useEffect, useRef, useState } from 'react';
import { trackPhoneClick } from '../../lib/tracking';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  const handleContactClick = () => {
    if (typeof window === 'undefined') return;

    trackPhoneClick();

    window.setTimeout(() => {
      window.location.href = 'tel:+4915214468574';
    }, 120);

    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window === 'undefined') return;

      const currentY = window.scrollY || window.pageYOffset;
      const lastY = lastScrollYRef.current;

      const THRESHOLD = 16;

      if (Math.abs(currentY - lastY) < THRESHOLD) {
        return;
      }

      if (currentY > lastY && currentY > 80) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const linkClasses =
    'relative text-[0.7rem] lg:text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:scale-x-0 after:bg-[#38BDF8] after:origin-center after:transition-transform after:duration-200 hover:after:scale-x-100';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#BFDBFE] shadow-[0_10px_35px_rgba(11,31,58,0.10)] transform transition-transform duration-300 ${
          isHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >

      {/* MAIN ROW */}
      <div className="max-w-7xl mx-auto px-4 md:px-3 py-2 md:py-3 flex items-center justify-between gap-4">
        {/* LEFT — LOGO */}
        <a href="/#hero" className="cursor-pointer flex items-center gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-10 h-10 md:w-11 md:h-11 rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#123E7A] flex items-center justify-center shadow-[0_10px_24px_rgba(11,31,58,0.18)]">
              <i className="ri-roadster-line text-[#38BDF8] text-lg md:text-xl" />
            </div>
            <div className="leading-none">
              <div className="text-[0.95rem] md:text-[1.05rem] tracking-[0.08em]">
                <span className="font-bold text-[#0B1F3A]">ABSCHLEPP</span>
                <span className="font-semibold text-[#123E7A]">DIREKT24</span>
              </div>
              <div className="text-[0.52rem] md:text-[0.58rem] uppercase tracking-[0.22em] text-[#64748B] mt-1">
                24/7 Berlin & Umgebung
              </div>
            </div>
          </div>
        </a>

        {/* DESKTOP NAV (ONEPAGER ANCHORS) */}
        <nav className="hidden md:flex items-center gap-8">
          <a
            href="/#hero"
            className={linkClasses}
          >
            Home
          </a>
          <a
            href="/#about"
            className={linkClasses}
          >
            Über uns
          </a>
          <a
            href="/#services"
            className={linkClasses}
          >
            Services
          </a>
          <a
            href="/#faq"
            className={linkClasses}
          >
            FAQ
          </a>
          <a
            href="/#kontakt"
            className={linkClasses}
          >
            Kontakt
          </a>
        </nav>

        {/* MOBILE MENU TOGGLE + CALL BUTTON */}
        <div className="md:hidden flex items-center gap-3">
          <button
            type="button"
            onClick={handleContactClick}
            aria-label="Jetzt anrufen"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0B1F3A] border border-[#123E7A] hover:bg-[#123E7A] transition-colors shadow-[0_10px_24px_rgba(11,31,58,0.24)]"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0 text-[#E0F2FE]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L8.03 9.97a16 16 0 0 0 6 6l1.52-1.26a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92z" />
            </svg>
            <span className="text-[0.7rem] font-medium tracking-[0.08em] text-[#E0F2FE] uppercase">
              Anrufen
            </span>
          </button>
          <button
            className="w-11 h-11 flex items-center justify-center bg-[#E0F2FE] border border-[#BFDBFE] hover:bg-[#DBEAFE] rounded-full transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Menü"
          >
            <span className="relative flex h-5 w-7 items-center justify-center">
              <span
                className={`absolute h-[2px] w-7 rounded-full bg-[#0B1F3A] transition-transform duration-300 ${
                  isMenuOpen ? 'translate-y-0 rotate-45' : '-translate-y-1.5 rotate-0'
                }`}
              />
              <span
                className={`absolute h-[2px] w-7 rounded-full bg-[#0B1F3A] transition-opacity duration-300 ${
                  isMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute h-[2px] w-7 rounded-full bg-[#0B1F3A] transition-transform duration-300 ${
                  isMenuOpen ? 'translate-y-0 -rotate-45' : 'translate-y-1.5 rotate-0'
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`md:hidden border-t border-[#BFDBFE] px-6 overflow-hidden transform transition-all duration-300 ease-out ${
          isMenuOpen
            ? 'bg-white/95 backdrop-blur-xl py-6 opacity-100 max-h-96'
            : 'bg-transparent backdrop-blur-0 py-0 opacity-0 max-h-0'
        }`}
      >
        <div className="flex items-center justify-between gap-6">
          <div className="flex flex-col space-y-5">
            <a
              href="/#hero"
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </a>
            <a
              href="/#about"
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Über uns
            </a>
            <a
              href="/#services"
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Services
            </a>
            <a
              href="/#faq"
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              FAQ
            </a>
            <a
              href="/#kontakt"
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#0B1F3A] hover:text-[#123E7A] transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Kontakt
            </a>
          </div>
        </div>
      </div>
      </header>
    </>
  );
}
